import { FEATURES } from 'appConstants';
import { CI_SCREEN_OVERVIEW } from 'containers/Intelligence/permissions/constants';
import { CI_ROOT } from 'containers/Setting/components/RoleFormModal/customerIntelligence';
import { OW_ROOT } from 'containers/Setting/components/RoleFormModal/overwatch';

/**
 * `aiWorkspace` is the `perx_ai_workspace` master flag, not `intelligence_suite` - the Suite is one
 * module inside the WorkSpace and cannot draw the container. Without the master a tenant gets no AI
 * WorkSpace at all (Figma variant `123:132`), so the two rows the redesign renamed and relocated -
 * Customers and Microsite - go back to their own names, since "Customer 360" and "Experience Studio"
 * only mean anything inside that framing. PLA-8844.
 */
export const getFullMenu = (aiWorkspace = true, intelligenceSuite = true) => [
  {
    key: 'agent_control_panel',
    url: '/p/agent-control-panel',
    svg: 'robot',
    description: 'Perx AI'
  },
  {
    key: 'language_selector',
    url: '/p/language_selector',
    svg: 'language_selector',
    description: 'Languages'
  },
  {
    key: 'intelligence_suite',
    url: '/p/intelligence/customer-insights',
    svg: 'bulb',
    description: 'Intelligence suite'
  },
  {
    key: 'customer_intelligence',
    url: '/p/intelligence/customer-insights',
    svg: 'intelligence_suite',
    description: 'Customer Insights'
  },
  {
    key: 'business_intelligence',
    url: '/p/business_intelligence/overview',
    svg: 'dashboard',
    description: 'Dashboard'
  },
  {
    key: 'quicksight',
    url: '/p/quicksight',
    svg: 'dashboard',
    description: 'Analytics'
  },
  {
    key: 'reports',
    url: '/p/reports',
    svg: 'reports',
    description: 'Reports'
  },
  {
    key: 'marketplace',
    url: '/p/marketplace',
    svg: 'marketplace',
    description: 'Marketplace'
  },
  {
    key: 'rewards',
    url: '/p/rewards',
    svg: 'rewards',
    description: 'Rewards'
  },
  {
    key: 'inventories',
    url: '/p/inventories',
    svg: 'inventory',
    description: 'Inventory'
  },
  {
    key: 'catalogs',
    url: '/p/catalogues',
    svg: 'catalogues',
    description: 'Catalogues'
  },
  {
    key: 'campaigns',
    url: '/p/campaigns',
    svg: 'campaigns',
    description: 'Campaigns'
  },
  {
    key: 'badges',
    url: '/p/badges',
    svg: 'badges',
    description: 'Badges'
  },
  {
    key: 'leaderboards',
    url: '/p/leaderboards',
    svg: 'leaderboards',
    description: 'Leaderboard'
  },
  {
    key: 'loyalties',
    url: '/p/loyalty',
    svg: 'loyalty',
    description: 'Loyalty'
  },
  {
    key: 'audiences',
    url: '/p/audiences',
    svg: 'audience',
    description: 'Audiences'
  },
  {
    key: 'transaction_rules',
    url: '/p/rules',
    svg: 'rules',
    description: 'Rules'
  },
  {
    key: 'cashback_rules',
    url: '/p/cashback_rules',
    svg: 'cashback',
    description: 'Cashback'
  },
  {
    key: 'merchants',
    url: '/p/merchants',
    svg: 'merchant',
    description: 'Merchant'
  },
  {
    key: 'customer_management',
    url: '/p/customers',
    // Customers only reads as "Customer 360" inside the Suite - that is what supplies the 360
    // treatment. With the Suite off it is the plain Customers module again. PLA-8844.
    svg: intelligenceSuite ? 'customer_360' : 'customers_group',
    description: intelligenceSuite ? 'Customer 360' : 'Customers'
  },
  {
    key: 'bulk_actions',
    url: '/p/bulk_actions',
    svg: 'bulk_actions',
    description: 'Bulk Action'
  },
  {
    key: 'partners',
    url: '/p/partners',
    svg: 'partner',
    description: 'Partners'
  },
  {
    key: 'microsites',
    url: '/p/microsite',
    svg: 'phone',
    description: aiWorkspace ? 'Experience Studio' : 'Microsite'
  },

  {
    key: 'points_redemptions',
    url: '/p/points_redemptions',
    svg: 'transfers',
    description: 'Points Redemptions'
  },
  {
    key: 'settings',
    url: '/p/settings/general/media',
    svg: 'settings',
    description: 'Settings'
  },
  {
    key: 'scheduled_tasks',
    url: '/p/scheduled-tasks',
    svg: 'scheduled_tasks',
    description: 'Scheduled Tasks'
  },
  {
    key: 'growth_planner',
    url: '/p/growth-planner',
    svg: 'dashboard',
    description: 'Growth Planner'
  },
  {
    key: 'config_studio',
    url: '/p/config-studio',
    svg: 'config_studio',
    description: 'Config Studio'
  },
  {
    key: 'perx_overwatch',
    url: '/p/overwatch',
    svg: 'perx_overwatch',
    description: 'Perx OverWatch'
  }
];

const canView = (permissions, resourceKey: string): boolean =>
  permissions?.[resourceKey]?.actions?.includes('view') === true;

// The Intelligence Suite entry sits second in the menu and its only destination needs the Customer
// Insights screen grant, which the root grant never implies (`conventions/permissions/gotchas.md
// § 4`). The rule lives here rather than in the sidebar's useMemo because every consumer of this
// list picks a landing destination off it — the post-signin push in `saga.ts` and the tenant-switch
// redirect in `App/index.tsx` — so a check kept in the sidebar would leave those two paths
// redirecting an unentitled user straight onto Forbidden. PLA-8815.
const isIntelligenceSuiteAvailable = (permissions, features): boolean =>
  features?.[FEATURES.INTELLIGENCE_SUITE.key] === true &&
  canView(permissions, CI_ROOT) &&
  canView(permissions, CI_SCREEN_OVERVIEW);

// The Suite as this operator has it: switched on AND granted. Customers takes its Customer 360 name
// and its place under the Suite only then; without the grant it keeps its Foundation row and name,
// matching the Customer Detail page, which keeps the legacy layout for the same operator.
export const isIntelligenceSuiteGranted = (permissions, features): boolean =>
  features?.[FEATURES.INTELLIGENCE_SUITE.key] === true && canView(permissions, CI_ROOT);

const isOverwatchAvailable = (permissions, features): boolean =>
  features?.[FEATURES.PERX_AI_WORKSPACE.key] === true &&
  features?.[FEATURES.LAYER_6_DASHBOARD.key] === true &&
  canView(permissions, OW_ROOT);

export const getFilteredMenu = (permissions = {}, features = {}) => {
  const permissionKeys = Object.keys(permissions);
  return getFullMenu(
    features?.[FEATURES.PERX_AI_WORKSPACE.key] === true,
    isIntelligenceSuiteGranted(permissions, features)
  ).filter(
    item =>
      permissionKeys.includes(item.key) &&
      !permissions?.[item.key]?.restricted_actions?.includes('index') &&
      (item.key !== CI_ROOT || isIntelligenceSuiteAvailable(permissions, features)) &&
      (item.key !== OW_ROOT || isOverwatchAvailable(permissions, features))
  );
};

/**
 * Key for the Perx Orbit row. It is deliberately absent from `getFullMenu`: Orbit has no route, so
 * a real entry would make it a candidate landing destination in `saga.ts` and `App/index.tsx` and
 * push a freshly signed-in user at a URL that resolves to nothing. The sidebar injects the row and
 * handles the click itself. PLA-8844.
 */
export const ORBIT_NAV_KEY = 'perx_orbit';

/**
 * A row, or a row with rows indented under it. The design nests Customer Insights and Customer 360
 * beneath `Intelligence suite`, which makes the sidebar three levels deep where it was two.
 * PLA-8844.
 */
export type NavEntry = string | { key: string; children: string[] };

export interface NavGroup {
  label: string;
  keys: NavEntry[];
}

/**
 * Sidebar grouping, per Figma `7:1447` (Perx Config Studio). This drives presentation ONLY — the
 * flat `getFilteredMenu` remains the source of truth for permissions and for the landing-destination
 * pick, so a key listed here still has to survive that filter to render. A key the design omits
 * (`language_selector`, `partners`) is simply absent from every group: its route keeps working, it
 * just no longer has a sidebar row.
 */
const AI_WORKSPACE_GROUPS: NavGroup[] = [
  {
    label: 'Perx AI WorkSpace',
    keys: [
      { key: 'intelligence_suite', children: ['customer_intelligence', 'customer_management'] },
      ORBIT_NAV_KEY,
      'growth_planner',
      'config_studio',
      'microsites',
      'perx_overwatch'
    ]
  },
  {
    label: 'Foundation',
    keys: [
      'language_selector',
      'business_intelligence',
      'quicksight',
      'reports',
      'audiences',
      'transaction_rules',
      'bulk_actions',
      'settings'
    ]
  },
  {
    label: 'Rewards Ecosystem',
    keys: ['rewards', 'inventories', 'marketplace', 'catalogs', 'partners']
  },
  {
    label: 'Gamified Engagements',
    keys: ['badges', 'campaigns', 'leaderboards']
  },
  {
    label: 'Loyalty mechanics',
    keys: ['loyalties', 'points_redemptions']
  },
  {
    label: 'Booster',
    keys: ['merchants', 'scheduled_tasks', 'cashback_rules']
  }
];

/**
 * The rows the master `perx_ai_workspace` flag owns outright. Growth Planner and Config Studio
 * carry no backend permission at all, so the flag is the only thing standing between them and
 * every tenant's sidebar. PLA-8844. Perx Overwatch is not one of them: it answers to its own
 * `perx_overwatch` flag and permission in `getFilteredMenu`. PLA-9674.
 */
export const AI_WORKSPACE_MENU_KEYS = ['growth_planner', 'config_studio'];

/**
 * The Intelligence Suite row is a child of the AI WorkSpace, not the umbrella: it carries no
 * backend permission either, but it answers to `intelligence_suite` on top of the master flag, so
 * a tenant can run the AI WorkSpace without the Intelligence Suite module. PLA-8844.
 */
export const INTELLIGENCE_SUITE_MENU_KEY = 'intelligence_suite';

/**
 * Without the master flag the whole Perx AI WorkSpace group is absent, header included - read
 * off Figma `123:132`, whose row stack starts at Foundation with no empty container. Its two
 * non-AI members keep their rows, in Foundation, so a tenant without the flag does not lose
 * Customers or Microsite from the nav. PLA-8844.
 */
/**
 * Settings closes the Foundation group. Rows relocated into Foundation have to land above it rather
 * than after it, or the flag state decides where Settings sits. PLA-8844.
 */
const FOUNDATION_TAIL_KEY = 'settings';

const intoFoundation = (group: NavGroup, keys: NavEntry[]): NavGroup => ({
  ...group,
  keys: [
    ...group.keys.filter(entry => entry !== FOUNDATION_TAIL_KEY),
    ...keys,
    ...group.keys.filter(entry => entry === FOUNDATION_TAIL_KEY)
  ]
});

export const getNavGroups = (aiWorkspace: boolean, intelligenceSuite = true): NavGroup[] => {
  if (!aiWorkspace) {
    return AI_WORKSPACE_GROUPS.filter(group => group.label !== 'Perx AI WorkSpace').map(group =>
      group.label === 'Foundation'
        ? intoFoundation(group, ['customer_management', 'microsites'])
        : group
    );
  }

  if (intelligenceSuite) {
    return AI_WORKSPACE_GROUPS;
  }

  /*
    Suite off: the Intelligence suite entry goes with it. Customers has to be moved back to
    Foundation explicitly - the sidebar promotes the children of a gated-away parent rather than
    dropping them, so left here it would stand alone in the WorkSpace under its 360 name. PLA-8844.
  */
  return AI_WORKSPACE_GROUPS.map(group => {
    if (group.label === 'Perx AI WorkSpace') {
      return { ...group, keys: group.keys.filter(entry => typeof entry === 'string') };
    }

    return group.label === 'Foundation' ? intoFoundation(group, ['customer_management']) : group;
  });
};
